import * as amplitude from '@amplitude/analytics-browser'
import compact from 'lodash/compact'

import { properCasifyString, snakeCasifyProperties } from '@procsea/common/utils'

import { useUser } from 'src/contexts/userContext'
import { isToggleEnabled } from 'src/hooks/utils/isToggleEnabled'

import { amplitudeInstance, historicalInstance } from './useSegmentInit'

// Analytics now runs on Amplitude, but the `segment` naming is kept on purpose:
// this swaps only the internals behind the same public interface. A generic
// rename (`useAnalytics`) is left for a follow-up MR.

const instances = [amplitudeInstance, historicalInstance]

export type Identify = <T = {}>(id: string, args: T) => void

export type Track = <T = {}>(name: string, properties?: T) => void

interface UseSegmentReturnValue {
  identify: Identify
  isEnabled: boolean
  page: (category: string, page: string) => void
  reset: () => void
  track: Track
}

export const MOCK_USE_SEGMENT: UseSegmentReturnValue = {
  identify: () => {},
  isEnabled: false,
  page: () => {},
  reset: () => {},
  track: () => {},
}

export const useSegment = (): UseSegmentReturnValue => {
  const user = useUser()

  const isTrackingDisabledForStaff =
    isToggleEnabled(window.DISABLE_TRACKING_FOR_STAFF) && !(user?.impersonatorIsStaff === false)

  if (isTrackingDisabledForStaff) {
    // Suppress all events (incl. default tracking) during staff impersonation.
    instances.forEach(i => i.setOptOut(true))

    return MOCK_USE_SEGMENT
  }

  instances.forEach(i => i.setOptOut(false))

  const track: Track = (name, properties) =>
    instances.forEach(i => i.track(properCasifyString(name), snakeCasifyProperties(properties)))

  const identify: Identify = (id, args) => {
    const userId = compact([window.USER_ID_PREFIX, id]).join('-')
    const identifyEvent = new amplitude.Identify()
    Object.entries(snakeCasifyProperties(args) ?? {}).forEach(([key, value]) => {
      identifyEvent.set(key, value as amplitude.Types.ValidPropertyType)
    })

    instances.forEach(i => {
      i.setUserId(userId)
      i.identify(identifyEvent)
    })
  }

  const reset = () => instances.forEach(i => i.reset())

  // No-op: page views come from Amplitude default tracking. Kept to preserve the interface.
  const page: UseSegmentReturnValue['page'] = () => {}

  return { identify, isEnabled: true, page, reset, track }
}
